<template>
  <ion-page id="selectvoices-page">
    <ion-content>

      <div v-if="showState == SHOW_EXPLAINTOUR">
        <ExplainTour :screen="explainTourScreen"
                      :wordsSpoken="explainTourWordsSpoken"
                      :wordsSpokenTrigger="explainTourWordsSpokenTrigger"
                    @toolbar-click="explainTourToolbarClick" />
      </div>
      <div v-show="showState == SHOW_VOICES" :class="{ 'text-dark' : isDark,'text-light' : !isDark }">
        <TopLine :style="styleTopline" :title="selectVoicesTitle" :microphoneIsMuted="microphoneIsMuted" @topline-click="toplineClick"/>
        <ion-list id="select-voices" :style="styleVoicesAvailable">
           <ion-item v-if="isLoading">
            <ion-label>
              <h1>Loading Available Voice List</h1>
            </ion-label>
          </ion-item>
          <ion-item>
            <ion-label color="danger">
              {{ translateEnglishWords("To Hear Voice : Say ' command voice # ' as in ' command voice 5 '") }}
            </ion-label>
          </ion-item>

          <ion-item v-for="item in voicesItems" v-bind:key="item.index" @click="onClick(item.index)"
                 :class="{
                  'selected-text-dark' : item.selected && isDark,
                  'selected-text-light' : item.selected && !isDark
                  }"
              >
            <ion-label>
              "{{ voiceWordString }} {{ item.index+1 }}" {{ item.displayName }} 
            </ion-label>
          </ion-item> 
        </ion-list>
        <div  :style="styleVoicesSelected">
          <ion-item>
            <ion-label>
              <b>{{ translateEnglishWords("Player") }}: </b>{{ playerDisplayName }}<br><b>{{ translateEnglishWords("Opponent") }}: </b>{{ opponentDisplayName }}
            </ion-label>
          </ion-item>
        </div>
        <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
      </div>
    </ion-content>
  </ion-page>
  
</template>

<script setup>

  // https://docs.cloud.google.com/text-to-speech/docs/list-voices-and-types

  import { IonPage, IonContent,IonList,IonItem,IonLabel  } from '@ionic/vue';
  import { computed } from "@vue/reactivity"

  import { ref, watch } from 'vue'
  import ExplainTour from '@/components/ExplainTour.vue'
  import ToolBar from '@/components/ToolBar.vue'
  import { useRouter } from 'vue-router';
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '@/stores/windowSizeStore';

  import { buildStyleToolbar, buildStyleTopline,buildStyleMiddle,availableWidthHeight, scrollElement,
    calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import TopLine from '@/components/TopLine.vue'
  import { parseWordsSaid , buildWordListFromChoiceList, parseNumberWords } from '@/composables/wordlistSupport.js'
  import { voiceAddMicrophoneSupport } from '@/composables/voicesSupport.js'
  import { translateEnglishWords } from '@/composables/translation';
  import { verifyMp3NameLanguage, getMp3Voices , findMp3Entry } from '@/composables/mp3Support.js'
  import { buildPieceLocationStrings, getPieceSetName , columnToNumber, 
        buildAvailableMoves, buildVerbalSan} from '@/composables/blindfoldSupport.js'
  const emit = defineEmits(['where-next','word-list','mic-is-muted','string-to-speak'])

  const props = defineProps({
      wordsSaid : {
        type : String,
        default : ""
      },
      wordsSaidTrigger : {
        type : Number,
        default : 0
      }
    })

  const wordsSaid = computed({get() {return props.wordsSaid}})
  const wordsSaidTrigger = computed({get() {return props.wordsSaidTrigger}})

  const connectionStore = useConnectionStore()  
  const windowSizeStore = useWindowSizeStore()

  const isDark = ref(connectionStore.getDarkmode != "light")
  const isMe = connectionStore.getIsMe

  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)
  emit("mic-is-muted",microphoneIsMuted.value)

  const translationLanguage = connectionStore.languageInUse

  const wordlistTrigger = ref(0)
  const wordlistVoices = "command scroll up down set player opponent voice explain done ignore mute listen one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen twenty thirty forty fifty sixty seventy eighty ninety";
  const wordlist = ref(wordlistVoices)
  emit("word-list",wordlistVoices)

  const SHOW_VOICES = 0
  const SHOW_EXPLAINTOUR = 1
  const showState = ref(SHOW_VOICES)

  const TOOLBAR_SET_PLAYER = 0
  const TOOLBAR_SET_OPPONENT = 1
  const TOOLBAR_EXPLAINTOUR = 2
  const TOOLBAR_BUILDMP3 = 3

  const toolbarElements = ref([
    { index : TOOLBAR_SET_PLAYER, label : translateEnglishWords("Set Player") ,disabled : true},
    { index : TOOLBAR_SET_OPPONENT, label : translateEnglishWords("Set Opponent") ,disabled : true},
    { index : TOOLBAR_EXPLAINTOUR, label : translateEnglishWords("Explain") ,disabled : false},
  ])

  if (isMe)
    toolbarElements.value.push( { index : TOOLBAR_BUILDMP3, label : "MP3" ,disabled : true})

  const toolbarTrigger = ref(0)
  const isLoading = ref(true)
  const currentSelectedVoice = ref(-1)
  var scrollTop = 0

  //const sampleText = translateEnglishWords("rook\tknight\tbishop\tqueen\tking\tpawn")
  const sampleText = translateEnglishWords("rook , knight , bishop , queen , king , pawn")

  var mp3Strings = []
  var mp3CurrentIndex = 0
  var currentVoiceIndex = 0
  var timerCanStop = false

  const selectVoicesTitle = ref(translateEnglishWords("Select Voices"))
  const voiceWordString = ref(translateEnglishWords("Voice"))

  const explainTourWordsSpoken = ref("")
  const explainTourScreen = ref("")
  const explainTourWordsSpokenTrigger = ref(0)

  const voicesItems = ref([])
  var voices = []
  var engineVoiceNumber = -1
  var playerVoiceNumber = -1

  const originalPlayerMp3Name = connectionStore.getPlayerMp3Name
  const originalPlayerMp3Lang = connectionStore.getPlayerMp3Lang
  const originalPlayerMp3Vendor = connectionStore.getPlayerMp3Vendor
  const originalOpponentMp3Name = connectionStore.getOpponentMp3Name
  const originalOpponentMp3Lang = connectionStore.getOpponentMp3Lang
  const originalOpponentMp3Vendor = connectionStore.getOpponentMp3Vendor

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleVoicesAvailable = ref(buildStyleMiddle(availableWidthHeight()))
  const styleVoicesSelected = ref(buildStyleMiddle(availableWidthHeight()))
  updateWidthHeights()

  const playerDisplayName = ref("Default")
  const opponentDisplayName = ref("Default")

   watch(wordsSaidTrigger, (newVal,oldVal) => {
     //console.log('*********** words said ',props.wordsSaid)
     processWords(props.wordsSaid)
   })
  watch(() => windowSizeStore.getWindowSize, () => {
      //console.log('*********** Explorer -- window size changes detected')
      updateWidthHeights()
  })

  doStartupCheck(false,afterStartupCallbackRoutine)

  function afterStartupCallbackRoutine(stuffChanged) {
    voicesItems.value = getMp3Voices(translationLanguage)
    const playerEntry = findMp3Entry(originalPlayerMp3Vendor,originalPlayerMp3Lang,originalPlayerMp3Name)
    const opponentEntry = findMp3Entry(originalOpponentMp3Vendor,originalOpponentMp3Lang,originalOpponentMp3Name)
    console.log("ENTRYS ",playerEntry,opponentEntry)

    playerDisplayName.value = originalPlayerMp3Name
    opponentDisplayName.value = originalOpponentMp3Name

    isLoading.value = false

    if (playerEntry != null) {
      playerDisplayName.value = playerEntry.displayName
    }
    if (opponentEntry != null) {
      opponentDisplayName.value = opponentEntry.displayName
    }

  }

  function updateWidthHeights() {

    const heightOfSelected = 50
    const availableSpace = availableWidthHeight()

    styleToolbar.value = buildStyleToolbar(availableSpace)
    styleTopline.value = buildStyleTopline(availableSpace)
    styleVoicesAvailable.value = buildStyleMiddle(availableWidthHeight())
    styleVoicesSelected.value = buildStyleMiddle(availableWidthHeight())

    const availableHeight = (availableSpace.height - heightOfSelected)
    const selectedTop = (availableSpace.topIndent + availableSpace.height - heightOfSelected)
    styleVoicesAvailable.value.height = availableHeight + "px"
    styleVoicesSelected.value.top =  selectedTop+ "px"
    styleVoicesSelected.value.height = heightOfSelected + "px"
  }

  function saySample(voiceNumber,text) {
    const oneVoice = voicesItems.value[voiceNumber]
    const lang = oneVoice.lang
    const name = oneVoice.name
    const vendor = oneVoice.vendor

    emit('string-to-speak',text,lang,name,vendor)
  }

  // START Explain Tour Routines

  function explainTourInitialize(errorText) {
    showState.value = SHOW_EXPLAINTOUR
    explainTourScreen.value = (errorText == null) ? "select_voices" : ("ERROR:" + errorText);
    wordlist.value = connectionStore.getExplainTourWords
    emit("word-list",wordlistVoices)
    wordlistTrigger.value++
  }

  function explainTourToolbarClick(whatClicked) {
    switch (whatClicked) {

      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "done" :
        showState.value = SHOW_VOICES
        wordlist.value = wordlistVoices
        emit("word-list",wordlistVoices)
        wordlistTrigger.value++
        break
    }
  }

  // END Explain Tour Routines

  function toplineClick(whatClicked) {
    //console.log("topline",whatClicked)
    switch (whatClicked) {
      case "done" :
        processWords("command done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        break

    }
  }

  function toolbarClick(arrayIndex) {
    switch (arrayIndex) {
      case TOOLBAR_BUILDMP3 :
        buildMP3()    
        break
      case TOOLBAR_SET_PLAYER :
        processWords("command set player")
        break
      case TOOLBAR_SET_OPPONENT :
        processWords("command set opponent")
        break
      case TOOLBAR_EXPLAINTOUR :
        processWords("command explain")
        break
    }
 
  }

  function onClick(index) {

    currentSelectedVoice.value = index
    console.log("click on ",index)
    const voiceId = voicesItems.value[index].voiceId

    // remove any selected
    for (var i=0; i < voicesItems.value.length; i++)
      voicesItems.value[i].selected = false

    voicesItems.value[index].selected = true
    //console.log("after click", voicesItems.value)

    saySample(index,sampleText)

    toolbarElements.value[TOOLBAR_SET_PLAYER].disabled = false
    toolbarElements.value[TOOLBAR_SET_OPPONENT].disabled = false

    if (isMe)
      toolbarElements.value[TOOLBAR_BUILDMP3].disabled = false

    toolbarTrigger.value++
  }

  function parseCommands(words,currentWordIndex) {
    
    //console.log("in parse commands",words,currentWordIndex)
    var currentWord = (words.length > currentWordIndex) ? words[currentWordIndex] : "xyz"
    var nextWord = (words.length > currentWordIndex+1) ? words[currentWordIndex+1] : "xyz"
    var nextNextWord = (words.length > currentWordIndex+2) ? words[currentWordIndex+2] : "xyz"

    for (var i = 0; i < words.length; i++) {
        if (currentWord == "ignore") {
            return ["ignore",""];
        }
        if (currentWord == "explain") {
            return ["explain",""]
        }
        if (currentWord == "done" || currentWord == "mute" || currentWord == "listen")
          return [currentWord,""]

        if (currentWord == "scroll") {
            if (nextWord == "up") {
                return ["scroll", "up"]
            }
            if (nextWord == "down") {
                return ["scroll", "down"]
            }
        }
        
        // add choices here
        if (currentWord == "set") {
          if (nextWord == "player" || nextWord == "opponent")
            return (["set",nextWord])
        }
        if (currentWord == "voice") {
          const voiceIndex = parseNumberWords(nextWord,nextNextWord)
          return ["voice", voiceIndex]
        }
       
        currentWord = nextWord;
        nextWord = nextNextWord;
        nextNextWord = (i < words.length - 3) ? words[i + 3] : "xyz";
    }
    return (["ignore",""]);
  }

  function processWords(text) {
    //console.log("text in engine Menu ",text)
    var returnArray = parseWordsSaid(text,parseCommands)
    //console.log("command found",returnArray)

    if (showState.value == SHOW_EXPLAINTOUR) {
      explainTourWordsSpoken.value = text
      explainTourWordsSpokenTrigger.value++
      return;
    }

    switch (returnArray[0]) {
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "scroll" :
        scrollElement("select-voices",returnArray[1])
        break
      case "voice" :
        const voiceIndex = returnArray[1] - 1
        if (voiceIndex < 0)
          break
        if (voiceIndex > voicesItems.value.length -1)
          break
        onClick(voiceIndex)
        break
      case "set" :
        if (currentSelectedVoice.value == -1)
          break
        var selectedVoice = voicesItems.value[currentSelectedVoice.value]
        //console.log("selected ",returnArray[1],selectedVoice)
        if (returnArray[1] == "player") {
          playerVoiceNumber = currentSelectedVoice.value
          playerDisplayName.value = selectedVoice.displayName
          connectionStore.setPlayerMp3(selectedVoice.name,selectedVoice.lang,selectedVoice.vendor)
        }
        else
        if (returnArray[1] == "opponent") {
          engineVoiceNumber = currentSelectedVoice.value
          opponentDisplayName.value = selectedVoice.displayName
          connectionStore.setOpponentMp3(selectedVoice.name,selectedVoice.lang,selectedVoice.vendor)
        }
        break
      case "explain" :
        explainTourInitialize(null)
        break
      case "done" :
        emit("where-next","/voicesmenu")
        //router.replace({ path: "/accountmenu"})
        break
    }
  }

  function startTimer() {
    
    function oneSecondInterval() {

      if (mp3CurrentIndex == mp3Strings.length)
        clearInterval(id)
      else {
        console.log("mp3 loop",mp3CurrentIndex,mp3Strings[mp3CurrentIndex])
        saySample(currentSelectedVoice.value,mp3Strings[mp3CurrentIndex])
        mp3CurrentIndex++
      }
    }
    var id = setInterval(oneSecondInterval, 3000)
  }

  // this isn't working to step through them all
  function startPerVoiceTimer() {
    
    // do one voice every 3 hours
    const interval = 3 * 3600 * 1000

    function oneInterval() {

      if (currentVoiceIndex == voicesItems.value.length)
        clearInterval(idInterval)
      else {
        onClick(currentVoiceIndex)
        buildMP3()
        currentVoiceIndex++
      }
    }
    var idInterval = setInterval(oneInterval, interval)
  }
  
  function buildMP3() {
    const pieceValues = [ "R","B", "N","K", "Q"];
    const rankValues = ["1", "2", "3", "4", "5", "6", "7", "8"];
    const fileValues = ["a", "b", "c", "d", "e", "f", "g", "h"];

    const fileWords = [ "letters", "words", "names"]

    mp3Strings = []

    var chessJS = {}
    var verbalString = ""

    // the two castles
    chessJS = { piece : "k", san : "O-O", promotion : "" }
    verbalString = buildVerbalSan(chessJS,fileWords[0],true)
    mp3Strings.push(verbalString)
    chessJS = { piece : "k", san : "O-O-O", promotion : "" }
    verbalString = buildVerbalSan(chessJS,fileWords[0],true)
    mp3Strings.push(verbalString)

    for (var words = 0; words <= 2; words++) {

      for (var file = 0; file < 8; file++) {
        for (var rank = 0; rank < 8; rank++) {

          // **** build pawns

          // move forward
          chessJS = {
                      piece : "p",
                      san : fileValues[file] + rankValues[rank] + ( (rank == 0 || rank == 7) ? "=Q" : ""),
                      promotion : (rank == 0 || rank == 7) ? "q" : ""
          }
          verbalString = buildVerbalSan(chessJS,fileWords[words],true)
          mp3Strings.push(verbalString)

          // captures from file before and file after
          if (file > 0) {
            chessJS.san = fileValues[file-1] + "x" + fileValues[file] + rankValues[rank] + ( (rank == 0 || rank == 7) ? "=Q" : ""),
            verbalString = buildVerbalSan(chessJS,fileWords[words],true)
            mp3Strings.push(verbalString)
          }
          if (file < 7) {
            chessJS.san = fileValues[file+1] + "x" + fileValues[file] + rankValues[rank] + ( (rank == 0 || rank == 7) ? "=Q" : ""),
            verbalString = buildVerbalSan(chessJS,fileWords[words],true)
            mp3Strings.push(verbalString)
          }

          chessJS.promotion = ""
          // the other pieces.  move & capture
          for (var piece = 0; piece < 5; piece++) {
            chessJS.piece = pieceValues[piece].toLowerCase()
            chessJS.san =  pieceValues[piece] + fileValues[file] + rankValues[rank] 
            verbalString = buildVerbalSan(chessJS,fileWords[words],true)
            mp3Strings.push(verbalString)
            chessJS.san =  pieceValues[piece] + "x" + fileValues[file] + rankValues[rank] 
            verbalString = buildVerbalSan(chessJS,fileWords[words],true)
            mp3Strings.push(verbalString)
          }
        }
      }
    }
    console.log("MP3 Strings", mp3Strings)
    mp3CurrentIndex = 0
    startTimer()
    //aySample(currentSelectedVoice.value,mp3Strings[0])
  }
</script>

<style scoped>

.selected-text-light {
  padding: 2px;
  border : solid 1px black;
  border-radius: 3px;
}

.selected-text-dark {
  padding: 2px;
  border : solid 1px #fff;
  border-radius: 3px;
}


</style>